import React, { useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import IconButton from "../common/IconButton";
import { ToggleButton } from "../../../../components";
import { ReactComponent as FloatIcon } from "../../../../assets/amazonEmbededView/float-icon.svg";
import { ReactComponent as DockLeftIcon } from "../../../../assets/amazonEmbededView/dock-left-icon.svg";
import { ReactComponent as DockRightIcon } from "../../../../assets/amazonEmbededView/dock-right-icon.svg";
import { ReactComponent as EmbedCenterIcon } from "../../../../assets/amazonEmbededView/embed-center-icon.svg";
import { ReactComponent as SidepanelIcon } from "../../../../assets/amazonEmbededView/sidepanel-icon.svg";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectExtension } from "../../../../features/extension/extension.selector";
import TagMenu from "../../../Settings/ProductAnalyzer/TagMenu";
import { NoteItem } from "../../partials/Notes/NoteItem";
import { INote } from "../../../../features/productAnalyzer/productAnalyzer.types";
import {
  selectProductNotes,
  selectProductTags,
} from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { useProductAnalyzer } from "../../../../contexts/productAnalyzerContext";
import { selectTags } from "../../../../features/settings/settings.selector";
import {
  createProductTagThunk,
  deleteProductTagThunk,
} from "../../../../features/productAnalyzer/productAnalyzer.thunk";
import { ITag } from "../../../../features/settings/settings.types";
import { colors } from "../config";

const MESSAGES = {
  FLOAT: "float",
  DOCK_LEFT: "dock_left",
  DOCK_RIGHT: "dock_right",
  EMBED_CENTER: "embed_center",
  SIDEPANEL: "sidepanel",
} as const;

const buttonStyle = {
  backgroundColor: colors.primary.main,
  border: `1px solid ${colors.primary.main}`,
  borderRadius: "6px",
  width: 77,
  height: 30,
  fontSize: "11.3px",
  fontWeight: 500,
  color: colors.text.white,
  flexShrink: 0,
  lineHeight: "10px",
  "&:hover": {
    backgroundColor: colors.primary.hover,
    borderColor: colors.primary.hover,
  },
  "&.Mui-disabled": {
    backgroundColor: colors.background.gray,
    borderColor: colors.border.light,
    color: colors.text.disabled,
    opacity: 0.6,
  },
};

export default function Footer() {
  const dispatch = useAppDispatch();
  const { mode } = useAppSelector(selectExtension);
  const { notes } = useAppSelector(selectProductNotes);
  const { asin } = useProductAnalyzer();
  const [tagMenuEl, setTagMenuEl] = useState<null | HTMLElement>(null);
  const isTagMenuOpened = Boolean(tagMenuEl);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isUpdatingTag, setIsUpdatingTag] = useState<string>();
  const tags = useAppSelector(selectTags);
  const productTags = useAppSelector(selectProductTags);

  const sendMessage = (type: string) => {
    if (typeof chrome !== "undefined" && chrome.runtime) {
      chrome.runtime.sendMessage({ type, mode });
    } else {
      // Fallback to postMessage if chrome.runtime is not available
      window.parent.postMessage({ type, mode }, "*");
    }
  };

  const handleFloat = () => {
    sendMessage(MESSAGES.FLOAT);
  };

  const handleDockLeft = () => {
    sendMessage(MESSAGES.DOCK_LEFT);
  };

  const handleDockRight = () => {
    sendMessage(MESSAGES.DOCK_RIGHT);
  };

  const handleSidepanel = () => {
    sendMessage(MESSAGES.SIDEPANEL);
  };

  const handleEmbedCenter = () => {
    sendMessage(MESSAGES.EMBED_CENTER);
  };

  const handleAddNoteClick = () => {
    setIsAdding(true);
  };

  const handleTagClick = (tag: ITag) => {
    setIsUpdatingTag(tag.id);
    if (productTags.includes(tag.id)) {
      dispatch(deleteProductTagThunk({ asin, tagId: tag.id }))
        .unwrap()
        .catch((err) => {
          if (err?.response?.status === 409) return;
          throw err;
        })
        .finally(() => setIsUpdatingTag(undefined));
    } else {
      dispatch(createProductTagThunk({ asin, tagId: tag.id }))
        .unwrap()
        .catch((err) => {
          if (err?.response?.status === 409) return;
          throw err;
        })
        .finally(() => setIsUpdatingTag(undefined));
    }
  };

  const isActive = (messageType: string) => mode === messageType;

  return (
    <Box display="flex" flexDirection="column" gap={0.5} py={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          gap={0.5}
        >
          <IconButton
            tooltip="Float"
            onClick={handleFloat}
            disabled={isActive(MESSAGES.FLOAT)}
            iconColor={colors.primary.main}
          >
            <FloatIcon style={{ width: "25px", height: "25px" }} />
          </IconButton>
          <IconButton
            tooltip="Dock left"
            onClick={handleDockLeft}
            disabled={isActive(MESSAGES.DOCK_LEFT)}
            iconColor={colors.primary.main}
          >
            <DockLeftIcon style={{ width: "25px", height: "25px" }} />
          </IconButton>
          <IconButton
            tooltip="Dock right"
            onClick={handleDockRight}
            disabled={isActive(MESSAGES.DOCK_RIGHT)}
            iconColor={colors.primary.main}
          >
            <DockRightIcon style={{ width: "25px", height: "25px" }} />
          </IconButton>
          <IconButton
            tooltip="Sidepanel"
            onClick={handleSidepanel}
            disabled={isActive(MESSAGES.SIDEPANEL)}
            iconColor={colors.primary.main}
          >
            <SidepanelIcon style={{ width: "25px", height: "25px" }} />
          </IconButton>
          <IconButton
            tooltip="Embed center"
            onClick={handleEmbedCenter}
            disabled={isActive(MESSAGES.EMBED_CENTER)}
            iconColor={colors.primary.main}
          >
            <EmbedCenterIcon style={{ width: "25px", height: "25px" }} />
          </IconButton>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={0.5}
          flexWrap="wrap"
        >
          {tags.map((tag) => (
            <ToggleButton
              value={tag.id}
              size="medium"
              selected={productTags.includes(tag.id)}
              onClick={() => handleTagClick(tag)}
              disabled={tag.id === isUpdatingTag}
              sx={{
                color: colors.primary.main,
                border: `1px solid ${colors.primary.main}`,
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                minWidth: "40px",
                padding: "2px 8px",

                "&:hover": {
                  backgroundColor: colors.primary.hover,
                  borderColor: colors.primary.hover,
                  color: colors.text.white,
                },

                "&.Mui-selected": {
                  backgroundColor: colors.primary.main,
                  color: colors.text.white,

                  "&:hover": {
                    backgroundColor: colors.primary.hover,
                  },
                },
              }}
            >
              {tag.caption}
            </ToggleButton>
          ))}
          <Button
            variant="contained"
            sx={buttonStyle}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setTagMenuEl(event.currentTarget);
            }}
          >
            Add Tags
          </Button>
          <Button
            variant="contained"
            sx={buttonStyle}
            onClick={() => handleAddNoteClick()}
            disabled={isAdding}
          >
            Add Notes
          </Button>
        </Box>
        <TagMenu
          open={isTagMenuOpened}
          anchorEl={tagMenuEl}
          tag={null}
          onClose={() => setTagMenuEl(null)}
          isEmbed={true}
        />
      </Box>
      {(!!notes.length || isAdding) && (
        <Stack gap={0.5}>
          {isAdding && (
            <NoteItem
              asin={asin}
              onClose={() => setIsAdding(false)}
              isEmbed={true}
            />
          )}
          {notes.map((note: INote, index: number) => (
            <NoteItem
              key={`note-item-${note.id}`}
              asin={asin}
              data={note}
              isEmbed={true}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
